import {isLabelValue, isLabelValueEmpty} from '../utils/checkObjects';
import {Material} from './Material';
import {Attachment} from './attachments_info/Attachment';
import {Account} from './financial_infos/Account';
import {AccountDetailsByMaterial} from './financial_infos/AccountDetailsByMaterial';
import {POSendingOptions} from './POSendingOptions';
import {FinancialInfo} from './FinancialInfo';
import {AttachmentsInfo} from './AttachmentsInfo';
import {OneTimeVendor} from './OneTimeVendor';
import {HeaderDetails} from './HeaderDetails';
import {NBSValidation} from './NBSValidation';
import {SBValidation} from './SBValidation';
import {convertOldWoB} from '../utils/wayOfBuying';
import {EMPTY_LABEL_VALUE, REQUEST_STATUSES} from '../utils/constants';

/**
 * @typedef DeliveryAddress
 * @type {Object}
 * @property {string} name
 * @property {string} name2
 * @property {string} street
 * @property {string} houseNumber
 * @property {string} postalCode
 * @property {string} city
 * @property {string} state
 * @property {string} country
 * @property {boolean=} alternateAddress
 */

/**
 * @typedef User
 * @type {Object}
 * @property {string} label
 * @property {string} value
 * @property {string} email
 */

/**
 * A class representing a purchase request
 */
export class PurchaseRequest {
    /**
     * Create a Purchase Request
     * @param {string} vendorNameOrPrJSON
     * @param {string=} vendorCode
     * @param {OneTimeVendor=} oneTimeVendor
     * @param {NBSValidation=} nbsValidation
     * @param {SBValidation=} sbValidation
     * @param {string=} creationDate
     * @param {string=} prNumber
     * @param {string=} projectTitle
     * @param {WaysOfBuying=} wayOfBuying
     * @param {User=} spender
     * @param {User=} requester
     * @param {string=} rejectionMsg
     * @param {string=} internalComment
     * @param {string=} externalComment
     * @param {HeaderDetails=} headerDetails
     * @param {POSendingOptions=} sendingOptions
     * @param {FinancialInfo=} financialInfo
     * @param {Material[]=} materials
     * @param {AttachmentsInfo=} attachmentsInfo
     * @param {string=} status
     * @param {string[]=} vendorContactEmails
     * @param {number=} supplierLeadTime
     * @param {boolean=} editableSupplier
     * @param {string=} source
     * @param {string=} contractID
     * @param {string=} lastActiveUser
     * @param {string=} sourcingEventID
     * @param {string=} buyer
     * @param {string=} submissionDate
     * @param {string=} EPRId
     * @param {string=} sourceFromApiKey
     * @param {string=} nonPslJustification
     * @param {string=} systemAlias
     * @param {string=} sourceToDisplay
     * @param {LabelValue=} prSource
     */
    constructor(
        vendorNameOrPrJSON,
        vendorCode,
        oneTimeVendor,
        nbsValidation,
        sbValidation,
        creationDate,
        prNumber,
        projectTitle,
        wayOfBuying,
        spender,
        requester,
        rejectionMsg,
        internalComment,
        externalComment,
        headerDetails,
        sendingOptions,
        financialInfo,
        materials,
        attachmentsInfo,
        status,
        vendorContactEmails,
        supplierLeadTime,
        editableSupplier,
        source,
        contractID,
        lastActiveUser,
        sourcingEventID,
        buyer,
        submissionDate,
        EPRId,
        sourceFromApiKey,
        nonPslJustification,
        systemAlias,
        sourceToDisplay,
        prSource,
    ) {
        if (arguments.length === 1) { // If we have only 1 argument, this should be a JSON representing the PR
            try {
                const parsedPR = JSON.parse(vendorNameOrPrJSON);
                const materials = [];
                parsedPR.materials.forEach((material) => {
                    materials.push(new Material(
                        material.id,
                        material.name,
                        material.description,
                        material.quantity,
                        material.unit,
                        material.baseUnit,
                        material.price,
                        material.currency,
                        material.per,
                        material.taxCode,
                        material.tax,
                        material.l1Category,
                        material.l3Category,
                        material.requestedDeliveryDate,
                        material.plant,
                        material.storageLocation,
                        material.deliveryAddress,
                        material.total,
                        material.freeItem,
                        material.transportCosts,
                        material.supplierPartId,
                        material.supplierPartAuxiliaryId,
                        material.arePriceDataFromST,
                        material.supplierComments,
                        material.internalComments,
                        material.exchangeCurrencyRate,
                        material.type,
                        material.supplierStatus,
                        material.pslId,
                        material.transportCostType,
                        material.transportCostConditions,
                        material.uniqueSourcingToolId,
                        material.ppmCostPlan,
                        material.taxAmount,
                        material.source,
                    ));
                });

                const mainAttachments = [];

                parsedPR.attachmentsInfo.mainAttachments.forEach((attachment) => {
                    mainAttachments.push(new Attachment(
                        attachment.file,
                        attachment.topic,
                        attachment.user,
                        attachment.date,
                        attachment.name,
                        attachment.userId,
                    ));
                });

                const competitiveBiddingJustificationAttachments = [];

                parsedPR.attachmentsInfo.competitiveBiddingJustificationAttachments.forEach((attachment) => {
                    competitiveBiddingJustificationAttachments.push(new Attachment(
                        attachment.file,
                        attachment.topic,
                        attachment.user,
                        attachment.date,
                        attachment.name,
                        attachment.userId,
                    ));
                });

                const accounts = [];
                parsedPR.financialInfo.accounts.forEach((account) => {
                    accounts.push(new Account(
                        account.itemCategory,
                        account.glNumber,
                        account.accountType,
                        account.wbs,
                        account.costCenter,
                        account.profitCenter,
                        account.allocateFor,
                        account.allocationType,
                        account.accountStatus,
                        account.consumptionLevel,
                        account.isFixed,
                    ));
                });

                const accountDetailsByMaterial = [];
                parsedPR.financialInfo.accountDetailsByMaterial.forEach((a) => {
                    const tmpAccounts = [];
                    if ('material_id' in a) {
                        a.accounts.forEach((account) => {
                            tmpAccounts.push(new Account(
                                account.itemCategory,
                                account.glNumber,
                                account.accountType,
                                account.wbs,
                                account.costCenter,
                                account.profitCenter,
                                account.allocateFor,
                                account.allocationType,
                                account.accountStatus,
                                account.consumptionLevel,
                                account.isFixed,
                            ));
                        });
                    }
                    accountDetailsByMaterial.push(new AccountDetailsByMaterial(
                        materials.find((material) => material.id === a.material_id),
                        tmpAccounts,
                    ));
                });

                this._vendorName = parsedPR.vendorName;
                this._vendorCode = parsedPR.vendorCode;
                this._oneTimeVendor = new OneTimeVendor(
                    parsedPR.oneTimeVendor.supplierName,
                    parsedPR.oneTimeVendor.address,
                    parsedPR.oneTimeVendor.phoneNumber,
                    parsedPR.oneTimeVendor.supplierType,
                );
                this._nbsValidation = new NBSValidation(
                    parsedPR.nbsValidation.operationalBuyers,
                    parsedPR.nbsValidation.denialInfo,
                    parsedPR.nbsValidation.operationalBuyersInDL ? parsedPR.nbsValidation.operationalBuyersInDL : [],
                    parsedPR.nbsValidation.obApprover,
                );
                this._sbValidation = new SBValidation(
                    parsedPR?.sbValidation?.strategicBuyers,
                    parsedPR?.sbValidation?.denialInfo,
                    parsedPR.sbValidation?.sbApprover,
                );
                this._creationDate = parsedPR.creationDate;
                this._prNumber = parsedPR.prNumber;
                this._projectTitle = parsedPR.projectTitle;
                this._wayOfBuying = convertOldWoB(parsedPR.wayOfBuying);
                this._spender = parsedPR.spender;
                this._requester = parsedPR.requester;
                this._rejectionMsg = parsedPR.rejectionMsg;
                this._headerDetails = new HeaderDetails(
                    parsedPR.headerDetails.pslCompanyCodes,
                    parsedPR.headerDetails.companyCode,
                    parsedPR.headerDetails.purchaseOrganization,
                    parsedPR.headerDetails.purchasingGroup,
                    parsedPR.headerDetails.businessUnit,
                    parsedPR.headerDetails.plants,
                    parsedPR.headerDetails.defaultPlant,
                    parsedPR.headerDetails.defaultStorageLocation,
                    typeof parsedPR.headerDetails.defaultDeliveryAddress === 'object' ? parsedPR.headerDetails.defaultDeliveryAddress : {},
                    parsedPR.headerDetails.defaultRequestedDeliveryDate,
                    parsedPR.headerDetails.validityStart,
                    parsedPR.headerDetails.validityEnd,
                    parsedPR.headerDetails.paymentTerms,
                    parsedPR.headerDetails.newPaymentTerms,
                    parsedPR.headerDetails.orderingAddress,
                    parsedPR.headerDetails.invoicePartner,
                    parsedPR.headerDetails.incoterms,
                    parsedPR.headerDetails.supplierStatus,
                    parsedPR.headerDetails.ers,
                    parsedPR.headerDetails.grBasedInvoiceVerification,
                    parsedPR.headerDetails.goodsReceipt,
                );
                this._internalComment = parsedPR.internalComment;
                this._externalComment = parsedPR.externalComment;
                this._sendingOptions = new POSendingOptions(
                    parsedPR.sendingOptions.requester,
                    parsedPR.sendingOptions.spender,
                    parsedPR.sendingOptions.other,
                    parsedPR.sendingOptions.otherAddress,
                    parsedPR.sendingOptions.supplier,
                    parsedPR.sendingOptions.supplierAddresses,
                    parsedPR.sendingOptions.p2pSupplier,
                    parsedPR.sendingOptions.pslSupplier,
                    parsedPR.sendingOptions.vmdSupplier,
                    parsedPR.sendingOptions.hidePrice,
                    parsedPR.sendingOptions.coversheet,
                    parsedPR.sendingOptions.p2pOther,
                );
                this._financialInfo = new FinancialInfo(
                    parsedPR.financialInfo.areAccountSplitByMaterial,
                    accountDetailsByMaterial,
                    accounts,
                );
                this._materials = materials;
                this._attachmentsInfo = new AttachmentsInfo(
                    mainAttachments,
                    competitiveBiddingJustificationAttachments,
                    parsedPR.attachmentsInfo.aribaID,
                    parsedPR.attachmentsInfo.cbFormID,
                    parsedPR.attachmentsInfo.exception,
                    parsedPR.attachmentsInfo.selectedException,
                    parsedPR.attachmentsInfo.complianceIndicator,
                );
                this._status = parsedPR.status;
                this._vendorContactEmails = parsedPR.vendorContactEmails || [parsedPR.vendorContactEmail];
                this._supplierLeadTime = parsedPR.supplierLeadTime || 0;
                this._source = parsedPR.source;
                this._contractID = parsedPR.contractID;
                this._lastActiveUser = parsedPR.lastActiveUser;
                this._sourcingEventID = parsedPR.sourcingEventID;
                this._buyer = parsedPR.buyer;
                this._editableSupplier = parsedPR.editableSupplier || Array.from(String(parsedPR.vendorCode)).every((char) => char === '9' ) || this._source === 'popup';
                this._submissionDate = parsedPR.submissionDate || '';
                this._EPRId = parsedPR.EPRId;
                this._sourceFromApiKey = parsedPR.sourceFromApiKey;
                this._nonPslJustification = parsedPR.nonPslJustification || '';
                this._systemAlias = parsedPR.systemAlias;
                this._sourceToDisplay = parsedPR.sourceToDisplay;
                this._prSource = isLabelValue(parsedPR.prSource) ? parsedPR.prSource : EMPTY_LABEL_VALUE;
                this.processPlantStorageAndDelivery();
                this.processPSLCompanyCodes();
                this.processPlants();
                this.fixAllocation();
            } catch (e) {
                console.error(e);
            }
        } else if (arguments.length === 35) { // If we have the 35 arguments, we can create the PR with all the infos
            this._vendorName = vendorNameOrPrJSON;
            this._vendorCode = vendorCode;
            this._oneTimeVendor = oneTimeVendor;
            this._nbsValidation = nbsValidation;
            this._sbValidation = sbValidation;
            this._creationDate = creationDate;
            this._prNumber = prNumber;
            this._projectTitle = projectTitle;
            this._wayOfBuying = wayOfBuying;
            this._spender = spender;
            this._requester = requester;
            this._rejectionMsg = rejectionMsg;
            this._headerDetails = headerDetails;
            this._internalComment = internalComment;
            this._externalComment = externalComment;
            this._sendingOptions = sendingOptions;
            this._financialInfo = financialInfo;
            this._materials = materials;
            this._attachmentsInfo = attachmentsInfo;
            this._status = status;
            this._vendorContactEmails = vendorContactEmails;
            this._supplierLeadTime = supplierLeadTime;
            this._source = source;
            this._contractID = contractID;
            this._lastActiveUser = lastActiveUser;
            this._sourcingEventID = sourcingEventID;
            this._buyer = buyer;
            this._editableSupplier = editableSupplier || Array.from(String(this.vendorCode)).every((char) => char === '9') || this._source === 'popup';
            this._submissionDate = submissionDate;
            this._EPRId = EPRId;
            this._sourceFromApiKey = sourceFromApiKey;
            this._nonPslJustification = nonPslJustification || '';
            this._systemAlias = systemAlias;
            this._sourceToDisplay = sourceToDisplay;
            this._prSource = isLabelValue(prSource) ? prSource : EMPTY_LABEL_VALUE;
            this.processPlantStorageAndDelivery();
            this.processPSLCompanyCodes();
            this.processPlants();
            this.fixAllocation();
        } else { // If we have neither 1 nor 35 arguments, we do not create the Purchase Request
            console.error('To instantiate a PurchaseRequest object, you have to give either the JSON data, or all the parameters');
        }
    }

    /**
     * Process company codes provided by PSL
     */
    processPSLCompanyCodes() {
        if (!Array.isArray(this._headerDetails.pslCompanyCodes)) {
            this._headerDetails.pslCompanyCodes = [];
        }
        if (this._headerDetails.pslCompanyCodes.length === 1 && isLabelValueEmpty(this._headerDetails.companyCode)) {
            this._headerDetails.companyCode = this._headerDetails.pslCompanyCodes[0];
        }
    }

    /**
     * Process company codes provided by PSL
     */
    processPlants() {
        if (!Array.isArray(this._headerDetails.plants)) {
            this._headerDetails.plants = [];
        }
        if (this._headerDetails.plants.length === 1 && isLabelValueEmpty(this._headerDetails.defaultPlant)) {
            this._headerDetails.defaultPlant = this._headerDetails.plants[0];
        }
    }

    /**
     * Because plant, storageLocation and deliveryAddress are stored both at the PR level and at the material level
     * this method processes some logic with the following rules:
     * - if the PR has none or one material, the values in the PR root are the valid ones
     * - if the PR has several materials, the values in the material are the valid ones and at the PR level they must be "Multiple"
     */
    processPlantStorageAndDelivery() {
        if (this._materials.length === 1) {
            this.materials[0].plant = this._headerDetails.defaultPlant;
            this.materials[0].storageLocation = this._headerDetails.defaultStorageLocation;
            this.materials[0].deliveryAddress = this._headerDetails.defaultDeliveryAddress;
        }
    }

    /**
     * if we have several items but not separated for accounting, we should not allow splitting account
     */
    fixAllocation() {
        if (!Array.isArray(this._financialInfo.accounts)) {
            this._financialInfo.accounts = [];
        }
    }

    /**
     * Get a JS object with all the information about the Purchase Request
     * @return {{requester: (User|*), spender: (User|*), source: (string|*), vendorName: (string|*), creationDate: (string|*), prNumber: (string|*), supplierLeadTime: (*|number|(function(!PurchaseRequest): number)|PRField|number|number), vendorCode: (string|*), internalComment: (string|*), headerDetails: {companyCode: LabelValue, incoterms: LabelValue, businessUnit: LabelValue, defaultRequestedDeliveryDate: string, validityEnd: string, purchaseOrganization: LabelValue, defaultDeliveryAddress: DeliveryAddress, invoicePartner: LabelValue, purchasingGroup: LabelValue, validityStart: string, defaultPlant: LabelValue, pslCompanyCodes: LabelValue[],plants: LabelValue[], orderingAddress: LabelValue, defaultStorageLocation: LabelValue, newPaymentTerms: LabelValue, paymentTerms: LabelValue}, materials: {quantity: string, description: string, storageLocation: ({label: string, value: string}|LabelValue|*), tax: string, requestedDeliveryDate: string, taxCode: LabelValue, l1Category: string, unit: string, total: string, deliveryAddress: ({}|DeliveryAddress|*), price: string, plant: ({label: string, value: string}|LabelValue|*), name: string, currency: {label: string, value: string}, id: number, per: string, l3Category: string}[], editableSupplier: (*|boolean|(function(!PurchaseRequest): boolean)|PRField|number|this is string[]|boolean), attachmentsInfo: {exception: boolean, mainAttachments: {date: string, file: string, name: string, topic: string, user: string}[], cbFormID: string, selectedException: string, aribaId: string, competitiveBiddingJustificationAttachments: {date: string, file: string, name: string, topic: string, user: string}[]}, oneTimeVendor: {supplierName: string, address: DeliveryAddress, phoneNumber: string, supplierType: string}, financialInfo: {}, contractID: (string|*), sendingOptions: {requester: boolean, other: boolean, supplierAddresses: string[], pslSupplier: boolean, spender: boolean, otherAddress: string, supplier: boolean, p2pSupplier: boolean, vmdSupplier: boolean, coversheet: string, hidePrice: boolean, buyer: boolean}, externalComment: (string|*), vendorContactEmails: (*|string[]|(function(!PurchaseRequest): string[])|PRField|number|({type: string}|*)[]|string[]), nbsValidation: {denyMessage: string, operationalBuyers: User[], operationalBuyersInDL: []}, projectTitle: (string|*), wayOfBuying: {currentValue: {label: string, value: string}, availableOptions: string[]}, status: (string|*, sourcingEventID: string), submissionDate: (string|*),  EPRId: (string|*), sourceFromApiKey: string, nonPslJustification: string}}
     */
    getPurchaseRequest() {
        return {
            vendorName: this._vendorName,
            vendorCode: this._vendorCode,
            oneTimeVendor: this._oneTimeVendor.getOneTimeVendor(),
            nbsValidation: this._nbsValidation.getNBSValidation(),
            sbValidation: this._sbValidation.getSBValidation(),
            creationDate: this._creationDate,
            prNumber: this._prNumber,
            projectTitle: this._projectTitle,
            wayOfBuying: this._wayOfBuying.getWayOfBuying(),
            spender: this._spender,
            requester: this._requester,
            rejectionMsg: this._rejectionMsg,
            internalComment: this._internalComment,
            externalComment: this._externalComment,
            headerDetails: this._headerDetails.getHeaderDetails(),
            sendingOptions: this._sendingOptions.getPOSendingOptions(),
            financialInfo: this._financialInfo.getFinancialInfo(),
            materials: this._materials.map((material) => material.getMaterial()),
            attachmentsInfo: this._attachmentsInfo.getAttachmentsInfo(),
            status: this._status,
            vendorContactEmails: this._vendorContactEmails,
            supplierLeadTime: this._supplierLeadTime,
            editableSupplier: this._editableSupplier,
            source: this._source,
            contractID: this._contractID,
            lastActiveUser: this._lastActiveUser,
            sourcingEventID: this._sourcingEventID,
            buyer: this._buyer,
            submissionDate: this._submissionDate || '',
            EPRId: this._EPRId,
            sourceFromApiKey: this._sourceFromApiKey,
            nonPslJustification: this._nonPslJustification,
            systemAlias: this._systemAlias,
            sourceToDisplay: this._sourceToDisplay,
            prSource: this._prSource,
        };
    }

    /**
     * Returns the Material corresponding to the transport costs
     * @static
     * @param {Array<Material>} materials - Materials of a PR
     * @return {Material}
     */
    static getTransportsCostsMaterial(materials) {
        const lastMaterial = materials[materials.length-1];
        return (lastMaterial !== undefined && lastMaterial.transportCosts) ? lastMaterial : null;
    }

    /**
     * Compute the total price of a given material
     * @static
     * @param {number | object} idxOrMaterial - Index of the material or the material itself
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total price
     */
    static getTotalMaterialPrice(idxOrMaterial, materials) {
        const material = typeof idxOrMaterial === 'object' ? idxOrMaterial : materials[idxOrMaterial];
        return material?.price && material?.per && !material?.freeItem ? PurchaseRequest.getTotalMaterialQuantity(material, materials) * (material.price || 0) * (material.exchangeCurrencyRate || 1) / (material.per || 1) : material?.freeItem ? 1 : 0;
    }

    /**
     * Compute the total price of a given material withouth the exchange currency rate
     * @static
     * @param {number | object} idxOrMaterial - Index of the material or the material itself
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total price
     */
    static getTotalMaterialPriceWithoutCurrencyExchange(idxOrMaterial, materials) {
        const material = typeof idxOrMaterial === 'object' ? idxOrMaterial : materials[idxOrMaterial];
        return material?.price && material?.per && !material?.freeItem ? PurchaseRequest.getTotalMaterialQuantity(material, materials) * (material.price || 0) / (material.per || 1) : material?.freeItem ? 1 : 0;
    }

    /**
     * Compute the total quantity of a given material
     * @static
     * @param {number | object} idxOrMaterial - Index of the material or the material itself
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total quantity
     */
    static getTotalMaterialQuantity(idxOrMaterial, materials) {
        const material = typeof idxOrMaterial === 'object' ? idxOrMaterial : materials[idxOrMaterial];
        return material?.quantity || 0;
    }

    /**
     * Compute the total price of all materials
     * @static
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total price of all materials
     */
    static getTotalPrice(materials) {
        return materials.reduce((s, material) => s + PurchaseRequest.getTotalMaterialPrice(material, materials), 0);
    }

    /**
     * Compute the total price of all materials withouth the currency exchange rate
     * @static
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total price of all materials
     */
    static getTotalPriceWithoutCurrencyExchange(materials) {
        return materials.reduce((s, material) => s + PurchaseRequest.getTotalMaterialPriceWithoutCurrencyExchange(material, materials), 0);
    }

    /**
     * Compute the total price of materials having Validated or Blank supplier status
     * @static
     * @param {Array<Material>} materials - Materials of a PR
     * @return {number} The total price of materials having Validated or Blank supplier status
     */
    static getTotalPriceForBlankAndValidated(materials) {
        return materials.reduce((s, material) => {
            const materialPrice = (material.supplierStatus === 'Validated' ||
            material.supplierStatus === 'Blank' ||
            !material.supplierStatus) ?
                PurchaseRequest.getTotalMaterialPrice(material, materials) :
                0;
            return s + materialPrice;
        }, 0);
    }

    /**
     * Compute the total quantity of all materials
     * @static
     * @param {Array<Material>} materials
     * @return {number} The total quantity of all materials
     */
    static getTotalQuantity(materials) {
        return materials.reduce((s, material) => s + PurchaseRequest.getTotalMaterialQuantity(material, materials), 0);
    }

    /**
     * Get the vendorName value.
     * @return {string} The vendorName value.
     */
    get vendorName() {
        return this._vendorName;
    }

    /**
     * Set the vendorName value.
     * @param {string} value to set
     */
    set vendorName(value) {
        this._vendorName = value;
    }

    /**
     * Get the vendorCode value.
     * @return {string} The vendorCode value.
     */
    get vendorCode() {
        return this._vendorCode;
    }

    /**
     * Set the vendorCode value.
     * @param {string} value to set
     */
    set vendorCode(value) {
        this._vendorCode = value;
    }

    /**
     * Get the oneTimeVendor value.
     * @return {OneTimeVendor} The vendorCode value.
     */
    get oneTimeVendor() {
        return this._oneTimeVendor;
    }

    /**
     * Set the oneTimeVendor value.
     * @param {OneTimeVendor} value to set
     */
    set oneTimeVendor(value) {
        this._oneTimeVendor = value;
    }

    /**
     * Get the creationDate value.
     * @return {string} The creationDate value.
     */
    get creationDate() {
        return this._creationDate;
    }

    /**
     * Set the creationDate value.
     * @param {string} value to set
     */
    set creationDate(value) {
        this._creationDate = value;
    }

    /**
     * Get the prNumber value.
     * @return {string} The prNumber value.
     */
    get prNumber() {
        return this._prNumber;
    }

    /**
     * Set the prNumber value.
     * @param {string} value to set
     */
    set prNumber(value) {
        this._prNumber = value;
    }

    /**
     * Get the projectTitle value.
     * @return {string} The projectTitle value.
     */
    get projectTitle() {
        return this._projectTitle;
    }

    /**
     * Set the projectTitle value.
     * @param {string} value to set
     */
    set projectTitle(value) {
        this._projectTitle = value;
    }

    /**
     * Get the wayOfBuying value.
     * @return {WaysOfBuying} The wayOfBuying value.
     */
    get wayOfBuying() {
        return this._wayOfBuying;
    }

    /**
     * Set the wayOfBuying value.
     * @param {WaysOfBuying} value to set
     */
    set wayOfBuying(value) {
        this._wayOfBuying = value;
    }

    /**
     * Get the spender value.
     * @return {{label: string, value: string, email: string}} The spender value.
     */
    get spender() {
        return this._spender;
    }

    /**
     * Set the spender value.
     * @param {{label: string, value: string, email: string}} value to set
     */
    set spender(value) {
        this._spender = value;
    }

    /**
     * Get the requester value.
     * @return {{label: string, value: string, email: string}} The requester value.
     */
    get requester() {
        return this._requester;
    }

    /**
     * Set the requester value.
     * @param {{label: string, value: string, email: string}} value to set
     */
    set requester(value) {
        this._requester = value;
    }

    /**
     * Get the rejection message value.
     * @return {string}} The rejection message value.
     */
    get rejectionMsg() {
        return this._rejectionMsg;
    }

    /**
     * Set the rejection message value.
     * @param {string} value to set
     */
    set rejectionMsg(value) {
        this._rejectionMsg = value;
    }

    /**
     * Get the header details value.
     * @return {HeaderDetails} The header details value.
     */
    get headerDetails() {
        return this._headerDetails;
    }

    /**
     * Set the header details value.
     * @param {HeaderDetails} value to set
     */
    set headerDetails(value) {
        this._headerDetails = value;
    }

    /**
     * Get the internalComment value.
     * @return {string} The internalComment value.
     */
    get internalComment() {
        return this._internalComment;
    }

    /**
     * Set the internalComment value.
     * @param {string} value to set
     */
    set internalComment(value) {
        this._internalComment = value;
    }

    /**
     * Get the externalComment value.
     * @return {string} The externalComment value.
     */
    get externalComment() {
        return this._externalComment;
    }

    /**
     * Set the externalComment value.
     * @param {string} value to set
     */
    set externalComment(value) {
        this._externalComment = value;
    }

    /**
     * Get the sendingOptions value.
     * @return {POSendingOptions} The sendingOptions value.
     */
    get sendingOptions() {
        return this._sendingOptions;
    }

    /**
     * Set the sendingOptions value.
     * @param {POSendingOptions} value to set
     */
    set sendingOptions(value) {
        this._sendingOptions = value;
    }

    /**
     * Get the financialInfo value.
     * @return {FinancialInfo} The financialInfo value.
     */
    get financialInfo() {
        return this._financialInfo;
    }

    /**
     * Set the financialInfo value.
     * @param {FinancialInfo} value to set
     */
    set financialInfo(value) {
        this._financialInfo = value;
    }

    /**
     * Get the materials value.
     * @return {Material[]} The materials value.
     */
    get materials() {
        return this._materials;
    }

    /**
     * Set the materials value.
     * @param {Material[]} value to set
     */
    set materials(value) {
        this._materials = value;
    }

    /**
     * Get the attachments_info value.
     * @return {AttachmentsInfo} The attachments_info value.
     */
    get attachmentsInfo() {
        return this._attachmentsInfo;
    }

    /**
     * Set the attachments_info value.
     * @param {AttachmentsInfo} value to set
     */
    set attachmentsInfo(value) {
        this._attachmentsInfo = value;
    }

    /**
     * Get the source value.
     * @return {string} The source value.
     */
    get source() {
        return this._source;
    }

    /**
     * Set the source value.
     * @param {string} value to set
     */
    set source(value) {
        this._source = value;
    }

    /**
     * Get the vendor contact emails
     * @return {string[]}
     */
    get vendorContactEmails() {
        return this._vendorContactEmails;
    }

    /**
     * Get the buyer contact emails
     * @return {string}
     */
    get buyer() {
        return this._buyer;
    }

    /**
     * Set the vendor contact emails
     * @param {string[]} value
     */
    set vendorContactEmails(value) {
        this._vendorContactEmails = value;
    }

    /**
     * Get the supplier lead time
     * @return {number}
     */
    get supplierLeadTime() {
        return this._supplierLeadTime;
    }

    /**
     * Set the supplier lead time
     * @param {number} value
     */
    set supplierLeadTime(value) {
        this._supplierLeadTime = value;
    }

    /**
     * Get whether the supplier of this PR can be edited
     * @return {boolean}
     */
    get editableSupplier() {
        return this._editableSupplier;
    }

    /**
     * Set whether the supplier for this PR can be edited
     * @param {boolean} value
     */
    set editableSupplier(value) {
        this._editableSupplier = value;
    }

    /**
     * Get the status of this PR
     * @return {string}
     */
    get status() {
        return this._status;
    }

    /**
     * Set the status of this PR
     * @param {string} value
     */
    set status(value) {
        this._status = value;
    }

    /**
     * Get the nbs validation value
     * @return {NBSValidation}
     */
    get nbsValidation() {
        return this._nbsValidation;
    }

    /**
     * Set the nbs validation value
     * @param {NBSValidation} value
     */
    set nbsValidation(value) {
        this._nbsValidation = value;
    }

    /**
     * Get the sb validation value
     * @return {SBValidation}
     */
    get sbValidation() {
        return this._sbValidation;
    }

    /**
     * Set the sb validation value
     * @param {SBValidation} value
     */
    set sbValidation(value) {
        this._sbValidation = value;
    }

    /**
     * Get the contract ID
     * @return {string}
     */
    get contractID() {
        return this._contractID;
    }

    /**
     * Set the contract ID
     * @param {string} value
     */
    set contractID(value) {
        this._contractID = value;
    }

    /**
     * Get the last active user
     * @return {string}
     */
    get lastActiveUser() {
        return this._lastActiveUser;
    }

    /**
     * Set the last active user
     * @param {string} value
     */
    set lastActiveUser(value) {
        this._lastActiveUser = value;
    }

    /**
         * Get the sourcingEventID value
         * @return {string}
         */
    get sourcingEventID() {
        return this._sourcingEventID;
    }

    /**
     * Get the OSubmission Date of this PR. It could be for SB/OB/SAP
     * @return {string}
     */
    get submissionDate() {
        return this._submissionDate;
    }

    /**
     * Set the Submission Date of this PR. It could be for SB/OB/SAP
     * @param {string} value
     */
    set submissionDate(value) {
        this._submissionDate = value;
    }

    /**
     * Get the EPRId
     * @return {string}
     */
    get EPRId() {
        return this._EPRId;
    }

    /**
     * Set the EPRId
     * @param {string} value
     */
    set EPRId(value) {
        this._EPRId = value;
    }

    /**
     * Get the source from API Key
     * @return {string}
     */
    get sourceFromApiKey() {
        return this._sourceFromApiKey;
    }

    /**
     * Set the source from API Key
     * @param {string} value
     */
    set sourceFromApiKey(value) {
        this._sourceFromApiKey = value;
    }

    /**
     * Get the Non-psl justification, reason and comment
     * @return {string}
     */
    get nonPslJustification() {
        return this._nonPslJustification;
    }

    /**
     * Set the Non-psl justification, reason and comment
     * @param {string} value
     */
    set nonPslJustification(value) {
        this._nonPslJustification = value;
    }

    /**
     * Get the source from API Key
     * @return {string}
     */
    get systemAlias() {
        return this._systemAlias;
    }

    /**
     * Set the source from API Key
     * @param {string} value
     */
    set systemAlias(value) {
        this._systemAlias = value;
    }

    /**
     * Get the source to display
     * @return {string}
     */
    get sourceToDisplay() {
        return this._sourceToDisplay;
    }

    /**
     * Set the source to display
     * @param {string} value
     */
    set sourceToDisplay(value) {
        this._sourceToDisplay = value;
    }

    /**
     * Get the PR source
     * @return {LabelValue}
     */
    get prSource() {
        return this._prSource;
    }

    /**
     * Set the PR source
     * @param {LabelValue} value
     */
    set prSource(value) {
        this._prSource = value;
    }
}

/**
 * @enum {string}
 */
PurchaseRequest.statuses = {
    created: REQUEST_STATUSES.CREATED,
    incompleteRequesterAndSpender: REQUEST_STATUSES.INCOMPLETE_REQUESTER_AND_SPENDER,
    incompleteSpender: REQUEST_STATUSES.INCOMPLETE_SPENDER,
    incompleteRequester: REQUEST_STATUSES.INCOMPLETE_REQUESTER,
    saved: REQUEST_STATUSES.SAVED,
    deniedByRequester: REQUEST_STATUSES.DENIED_BY_REQUESTER,
    deniedBySB: REQUEST_STATUSES.DENIED_BY_SB,
    deniedByOB: REQUEST_STATUSES.DENIED_BY_OB,
    pendingRequesterAndSpenderCompletion: REQUEST_STATUSES.PENDING_REQUESTER_AND_SPENDER_COMPLETION,
    pendingSpenderCompletion: REQUEST_STATUSES.PENDING_SPENDER_COMPLETION,
    pendingRequesterCompletion: REQUEST_STATUSES.PENDING_REQUESTER_COMPLETION,
    pendingRequesterCompletionSaved: REQUEST_STATUSES.PENDING_REQUESTER_COMPLETION_SAVED,
    pendingSB: REQUEST_STATUSES.PENDING_SB_APPROVAL,
    pendingNbs: REQUEST_STATUSES.PENDING_OB_APPROVAL,
    submitted: REQUEST_STATUSES.SUBMITTED,
};

/**
 * @enum {string}
 */
PurchaseRequest.tab = {
    open: 'open requests',
    pendingSb: 'pending sb approval',
    submitted: 'submitted requests',
};

